import axios from 'axios';
import EventBus from 'eventing-bus';
import { setCallLogs } from './index';
import { all, takeEvery, call, put } from 'redux-saga/effects';

//get all phones
function* getAllPhones() {
    const { error, response } = yield call(getCall, `/automation/phone`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch phones');
    else if (response) yield put({ type: 'SET_ALL_PHONES', payload: response['data']['body'] || [] });
}

// Batch fetch usage + Twilio-side health for all workspace numbers in one request.
// Returns { byId, accountAlert } — byId keyed by phoneId; accountAlert set when Twilio is blocking account-wide.
function* getPhoneHealth({ callback }) {
    const { error, response } = yield call(getCall, `/automation/phone/health`);
    if (error) { if (callback) callback(null, error); }
    else if (response) {
        const body = response['data']['body'] || {};
        const byId = {};
        (body['numbers'] || []).forEach(r => { byId[r['phoneId']] = r; });
        if (callback) callback({ byId, accountAlert: body['accountAlert'] || null });
    }
}

// Create a new phone
function* createPhone({ payload }) {
    yield put({ type: 'SET_LOADING', payload: { isLoading: true, message: 'Creating phone...' } });
    const { error, response } = yield call(postCall, { path: `/automation/phone/createPhone`, payload: payload['newForm'] });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message']);
    else {
        yield put({ type: 'GET_ALL_PHONES' });
        payload['successCallback'](response['data']['body']);
        // Publish phone created event for real-time updates
        EventBus.publish('phone:created', response['data']['body']);
    }
    yield put({ type: 'SET_LOADING', payload: { isLoading: false, message: '' } });
}

// Update a phone by ID
function* updatePhone({ payload }) {
    const id = payload['id'] || payload['_id'];
    const data = payload['data'] || payload;
    const { error, response } = yield call(putCall, { path: `/automation/phone/update/${id}`, payload: data });
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message']);
    else {
        yield put({ type: 'GET_ALL_PHONES' });
        EventBus.publish('phone:updated', response['data']['body']);
    }
}

// Update multiple phones with the same settings, reusing the single-update endpoint per ID
function* updatePhoneBulk({ payload }) {
    const { ids, data, onSuccess } = payload;
    const results = yield all(ids.map(id => call(putCall, { path: `/automation/phone/update/${id}`, payload: data })));
    const failed = results.find(r => r['error']);
    if (failed) EventBus.publish('error', failed['error']?.['response']?.['data']?.['message'] || 'Failed to update some phone numbers');
    yield put({ type: 'GET_ALL_PHONES' });
    if (onSuccess) onSuccess();
}

// Delete a phone by ID
function* deletePhone({ payload }) {
    const id = payload['id'] || payload;
    yield put({ type: 'SET_LOADING', payload: { isLoading: true, message: 'Deleting phone...' } });
    const { error, response } = yield call(deleteCall, `/automation/phone/delete/${id}`);
    if (error) EventBus.publish('error', error['response']?.['data']?.['message']);
    else {
        EventBus.publish('success', response['data']['message']);
        EventBus.publish('phone:deleted', id);
    }
    yield put({ type: 'GET_ALL_PHONES' });
    yield put({ type: 'SET_LOADING', payload: { isLoading: false, message: '' } });
}

// Link a phone to a sales account
function* linkPhone({ payload }) {
    const { id, salesAccountId } = payload;
    const { error } = yield call(putCall, { path: `/automation/phone/link/${id}`, payload: { salesAccountId } });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to link phone');
    else yield put({ type: 'GET_ALL_PHONES' });
}

// Unlink a phone from its sales account
function* unlinkPhone({ payload }) {
    const id = payload['id'] || payload;
    const { error } = yield call(putCall, { path: `/automation/phone/unlink/${id}`, payload: {} });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to unlink phone');
    else yield put({ type: 'GET_ALL_PHONES' });
}

//get all call logs of specific user
function* getCallLogs({ payload }) {
    const { error, response } = yield call(postCall, { path: `/automation/phone/callLogs`, payload });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message']);
    else yield put(setCallLogs(response['data']['body']));
}

// Create an outbound call
function* createOutboundCall({ payload }) {
    yield put({ type: 'SET_LOADING', payload: { isLoading: true, message: `Creating outbound call to ${payload['phoneNumber']}` } });
    const { error, response } = yield call(postCall, { path: `/automation/phone/createCall`, payload });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message']);
    else EventBus.publish('success', response['data']['message']);
    yield put({ type: 'SET_LOADING', payload: { isLoading: false, message: '' } });
}

// Create a demo call
function* createDemoCall({ payload }) {
    yield put({ type: 'SET_LOADING', payload: { isLoading: true, message: `Creating demo call to ${payload['phone']}` } });
    const { error, response } = yield call(postCall, { path: `/automation/phone/demoCall`, payload });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message']);
    else EventBus.publish('success', response['data']['message']);
    yield put({ type: 'SET_LOADING', payload: { isLoading: false, message: '' } });
}

// Get Twilio Voice access token
function* getTwilioVoiceToken() {
    const { error, response } = yield call(postCall, { path: `/automation/phone/voice/token`, payload: {} });
    if (error) {
        const errorMessage = error['response']?.['data']?.['message'] || 'Failed to get Twilio Voice token';
        // Pass error message to reducer to display in softphone UI instead of toast
        yield put({ type: 'SET_TWILIO_VOICE_TOKEN', payload: { token: null, error: errorMessage } });
    } else {
        yield put({ type: 'SET_TWILIO_VOICE_TOKEN', payload: response['data']['body'] });
    }
}

// Get softphone contacts (separate from main contacts page)
function* getSoftphoneContacts({ payload }) {
    const { page = 1, limit = 50 } = payload || {};
    const { error, response } = yield call(getCall, `/objects/contacts?page=${page}&limit=${limit}`);
    if (error) EventBus.publish('error', error['response']?.['data']?.['message']);
    else yield put({ type: 'SET_SOFTPHONE_CONTACTS', payload: { ...response['data']['body'], page } });
}

// Get softphone call logs (separate from main call logs page)
function* getSoftphoneCallLogs({ payload }) {
    const { error, response } = yield call(postCall, { path: `/automation/phone/callLogs`, payload: { ...payload, includeContact: true } });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message']);
    else yield put({ type: 'SET_SOFTPHONE_CALL_LOGS', payload: { ...response['data']['body'], page: payload?.page || 1 } });
}

function* bulkDeleteCallLogs({ payload }) {
    const { ids, onSuccess } = payload;
    const { error } = yield call(postCall, { path: `/automation/phone/callLogs/bulk-delete`, payload: { ids } });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to delete call logs');
    else { EventBus.publish('success', 'Call logs deleted'); if (onSuccess) onSuccess(); }
}

// Watcher for phone actions
function* actionWatcher() {
    yield takeEvery('UPDATE_PHONE', updatePhone);
    yield takeEvery('UPDATE_PHONE_BULK', updatePhoneBulk);
    yield takeEvery('DELETE_PHONE', deletePhone);
    yield takeEvery('CREATE_PHONE', createPhone);
    yield takeEvery('LINK_PHONE', linkPhone);
    yield takeEvery('UNLINK_PHONE', unlinkPhone);
    yield takeEvery('GET_CALL_LOGS', getCallLogs);
    yield takeEvery('BULK_DELETE_CALL_LOGS', bulkDeleteCallLogs);
    yield takeEvery('GET_ALL_PHONES', getAllPhones);
    yield takeEvery('GET_PHONE_HEALTH', getPhoneHealth);
    yield takeEvery('CREATE_DEMO_CALL', createDemoCall);
    yield takeEvery('CREATE_OUTBOUND_CALL', createOutboundCall);
    yield takeEvery('GET_TWILIO_VOICE_TOKEN', getTwilioVoiceToken);
    yield takeEvery('GET_SOFTPHONE_CONTACTS', getSoftphoneContacts);
    yield takeEvery('GET_SOFTPHONE_CALL_LOGS', getSoftphoneCallLogs);
}

// Root saga for phone actions
export default function* rootSaga() { yield all([actionWatcher()]) }

// Helper functions for API calls
function postCall({ path, payload }) {
    return axios.post(path, payload).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}

function getCall(path) {
    return axios.get(path).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}

function deleteCall(path) {
    return axios.delete(path).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}

function putCall({ path, payload, headers = {} }) {
    return axios.put(path, payload, { headers }).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}